// @ts-strict-ignore
import type { AuditActionParams } from '../composables'
import type { AuditTypeEnum } from '../enums'
import type { AuditItem, AuditState, ReadAuditPayload } from '../models'
import type { AuditAdapterProvider } from '../providers/interfaces'
import type { IAuditService } from '../services/interfaces'
import { CONTAINER } from '../config/inversify.config'
import { TYPES } from '../config/inversify.types'
import { ApiService } from './api.service'

export class AuditService extends ApiService implements IAuditService {
  private static readonly ENDPOINTS = {
    audit: '/audit',
  }

  async auditAction({ action, tool, params }: AuditActionParams): Promise<void> {
    const { instanceId } = this.contextProvider()
    const encodedParams = {
      ...params,
      payload: {
        ...params.payload,
        action: params.payload.action.toUpperCase(),
        objectType: params.payload.objectType.toUpperCase(),
      },
    }
    await this.fetch<AuditState>(
      `/${instanceId}/${tool}/${action}`,
      undefined,
      {
        method: 'POST',
        body: JSON.stringify(encodedParams),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
      true,
    )
  }

  async readAudit(payload: ReadAuditPayload): Promise<Array<AuditItem>> {
    const { startDate, endDate, types, actions, query, strictMatching, ...rest } = payload
    const requestParams = {
      ...(typeof strictMatching === 'boolean' && { strict_matching: strictMatching }),
      ...(!!startDate && { startDate: startDate.toISOString() }),
      ...(!!endDate && { endDate: endDate.toISOString() }),
      ...(!!types?.length && { types: types.join(',') }),
      ...(!!actions?.length && { actions: actions.join(',') }),
      ...(!!query && { query }),
      ...rest,
    }
    const { instanceId } = this.contextProvider()
    const auditResponse = await this.fetch<AuditState>(
      `${AuditService.ENDPOINTS.audit}/${instanceId}`,
      requestParams,
      {},
      true,
    )
    return auditResponse.data.data.map(responseDataItem =>
      this.decodeResponseDataItem(responseDataItem),
    )
  }

  private decodeResponseDataItem(responseDataItem: AuditItem): AuditItem {
    const payload = responseDataItem.payload
    const adapter = CONTAINER.get<AuditAdapterProvider>(TYPES.AuditAdapterProvider)(
      payload.objectType.toLowerCase() as AuditTypeEnum,
    )
    const previousState = payload.previousState
    const nextState = payload.nextState

    const decodedPayload: AuditItem['payload'] = { ...payload }

    if (adapter) {
      if (previousState) {
        decodedPayload.previousState = adapter.decode(previousState) as Record<string, unknown>
      }
      if (nextState) {
        decodedPayload.nextState = adapter.decode(nextState) as Record<string, unknown>
      }
    }

    return {
      ...responseDataItem,
      date: new Date(responseDataItem.date),
      payload: decodedPayload,
    }
  }
}
